import { pipe } from '@helo/utils'

import { colors } from '../../../theme/colors'
import {
  type ChartColorPalette,
  type ColorPatternName,
  Colors,
  colorPaletteOptions,
} from '../../Colors'
import { Options } from '../../Options'
import { RenderPipeline } from '../../RenderPipeline'
import type { ChartDataPoint, ChartDataSort } from '../ChartData'
import { chartDataOptions } from '../ChartData/ChartData.options'
import { ChartDataParts } from '../ChartData/ChartData.parts'
import { chartDataLabelOptions } from '../ChartDataLabel/ChartDataLabel.options'
import { ChartLineConstants } from '../ChartLine/ChartLine.constants'
import type { ChartSeriesOutput } from '../ChartSeries'
import { ChartSeriesUtils } from '../ChartSeries'
import type {
  ChartPieInput,
  ChartPieOutput,
  ResolvedChartPieSlice,
} from './ChartPie'
import { ChartPieConstants } from './ChartPie.constants'
import { chartPieOptions } from './ChartPie.options'

export type ChartPieSourcePoint = ChartDataPoint & {
  label: string
}

export type AggregatePieSlicesInput = {
  points: ChartPieSourcePoint[]
  dataTopN?: number
  dataSort: ChartDataSort
  otherThresholdPct: number
  colorPattern: ColorPatternName
  otherColorLight: string
  otherColorDark: string
  isDark?: boolean
  d3StartAngle?: number
  d3EndAngle?: number
}

const validValue = (
  point: ChartPieSourcePoint,
): point is ChartPieSourcePoint & { y: number } =>
  point.y != null && Number.isFinite(point.y) && point.y > 0

const sortPoints = (
  points: ChartPieSourcePoint[],
  dataSort: ChartDataSort,
): ChartPieSourcePoint[] => {
  if (dataSort === 'natural') return [...points]

  return [...points].sort((left, right) => {
    if (dataSort === 'dimension_asc' || dataSort === 'dimension_desc') {
      const comparison = left.label.localeCompare(right.label)
      return dataSort === 'dimension_asc' ? comparison : -comparison
    }

    const comparison = (left.y ?? 0) - (right.y ?? 0)
    return dataSort === 'measure_asc' ? comparison : -comparison
  })
}

const aggregateOtherItems = (
  points: ChartPieSourcePoint[],
): Array<{ name: string; value: number }> =>
  [...points]
    .sort((left, right) => (right.y ?? 0) - (left.y ?? 0))
    .map(point => ({
      name: point.label,
      value: point.y ?? 0,
    }))

/** Top-N and sub-threshold slices roll into a single Other bucket. */
const partitionVisibleAndOther = ({
  points,
  dataTopN,
  otherThresholdPct,
}: {
  points: ChartPieSourcePoint[]
  dataTopN?: number
  otherThresholdPct: number
}): {
  visible: Array<ChartPieSourcePoint & { y: number }>
  otherPoints: Array<ChartPieSourcePoint & { y: number }>
} => {
  const measured = points.filter(validValue)
  const total = measured.reduce((sum, point) => sum + point.y, 0)

  if (dataTopN != null) {
    const sorted = [...measured].sort((left, right) => right.y - left.y)
    if (sorted.length <= dataTopN) {
      return { visible: sorted, otherPoints: [] }
    }
    return {
      visible: sorted.slice(0, dataTopN),
      otherPoints: sorted.slice(dataTopN),
    }
  }

  const visible: Array<ChartPieSourcePoint & { y: number }> = []
  const otherPoints: Array<ChartPieSourcePoint & { y: number }> = []

  for (const point of measured) {
    const pct = total > 0 ? (point.y / total) * 100 : 0
    if (pct >= otherThresholdPct) {
      visible.push(point)
    } else {
      otherPoints.push(point)
    }
  }

  return { visible, otherPoints }
}

const assignSliceColors = ({
  visible,
  hasOther,
  colorPattern,
  otherColor,
}: {
  visible: ChartPieSourcePoint[]
  hasOther: boolean
  colorPattern: ColorPatternName
  otherColor: string
}): string[] => {
  const pattern = Colors.getPattern(colorPattern)
  const domain = visible.map(point => point.x)
  const mapped = Colors.mapDomain(domain, pattern)
  const palette = mapped.colors
  const colorsForVisible = visible.map(
    (_point, index) =>
      palette[index] ?? pattern.colors[index % pattern.colors.length]!,
  )
  return hasOther ? [...colorsForVisible, otherColor] : colorsForVisible
}

const computeSliceAngles = ({
  values,
  d3StartAngle = ChartPieConstants.d3StartAngle,
  d3EndAngle = ChartPieConstants.d3EndAngle,
}: {
  values: number[]
  d3StartAngle?: number
  d3EndAngle?: number
}): Array<{ startAngle: number; endAngle: number; midAngle: number }> => {
  const total = values.reduce((sum, value) => sum + value, 0)
  const span = d3EndAngle - d3StartAngle
  if (total <= 0) return []

  let cursor = d3StartAngle
  return values.map(value => {
    const sliceSpan = (value / total) * span
    const startAngle = cursor
    const endAngle = cursor + sliceSpan
    cursor = endAngle
    return {
      startAngle,
      endAngle,
      midAngle: startAngle + sliceSpan / 2,
    }
  })
}

export const mapPieColorPalette = (
  palette: ChartColorPalette,
): ColorPatternName => Colors.mapColorPalette(palette)

/** Resolve ranked slices with colors and pre-calculated d3 angles. */
const aggregatePieSlices = ({
  points,
  dataTopN,
  dataSort,
  otherThresholdPct,
  colorPattern,
  otherColorLight,
  otherColorDark,
  isDark = false,
  d3StartAngle = ChartPieConstants.d3StartAngle,
  d3EndAngle = ChartPieConstants.d3EndAngle,
}: AggregatePieSlicesInput): ResolvedChartPieSlice[] => {
  const sorted = sortPoints(points.filter(validValue), dataSort)
  const { visible, otherPoints } = partitionVisibleAndOther({
    points: sorted,
    dataTopN,
    otherThresholdPct,
  })
  const otherTotal = otherPoints.reduce((sum, point) => sum + (point.y ?? 0), 0)
  const hasOther = otherTotal > 0
  const otherColor = isDark ? otherColorDark : otherColorLight
  const sliceColors = assignSliceColors({
    visible,
    hasOther,
    colorPattern,
    otherColor,
  })

  const entries: Array<{
    key: string
    label: string
    value: number
    color: string
    isOther: boolean
    otherItems?: Array<{ name: string; value: number }>
  }> = visible.map((point, index) => ({
    key: point.x,
    label: point.label,
    value: point.y ?? 0,
    color: sliceColors[index]!,
    isOther: false,
  }))

  if (hasOther) {
    entries.push({
      key: '__other__',
      label: 'Other',
      value: otherTotal,
      color: sliceColors[sliceColors.length - 1]!,
      isOther: true,
      otherItems: aggregateOtherItems(otherPoints),
    })
  }

  const total = entries.reduce((sum, entry) => sum + entry.value, 0)
  const angles = computeSliceAngles({
    values: entries.map(entry => entry.value),
    d3StartAngle,
    d3EndAngle,
  })
  const pattern = Colors.getPattern(colorPattern)

  return entries.map((entry, index) => {
    const angle = angles[index]!
    const percent = total > 0 ? (entry.value / total) * 100 : 0
    return {
      key: entry.key,
      label: entry.label,
      value: entry.value,
      percent,
      color: entry.color,
      highlightColor: Colors.getHighlight(pattern),
      startAngle: angle.startAngle,
      endAngle: angle.endAngle,
      midAngle: angle.midAngle,
      isOther: entry.isOther,
      otherItems: entry.otherItems,
      pointRadius: ChartLineConstants.defaultPointRadius,
      lineWidth: ChartLineConstants.defaultLineWidth,
      visualization: 'pie' as const,
    }
  })
}

const percentLabelPosition = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  offset = ChartPieConstants.percentLabelOffset,
}: {
  cx: number
  cy: number
  midAngle: number
  outerRadius: number
  offset?: number
}) => {
  const radius = outerRadius + offset
  return {
    x: cx + radius * Math.cos(midAngle),
    y: cy + radius * Math.sin(midAngle),
  }
}

const pieFeatureOptions = {
  ...colorPaletteOptions,
  ...chartDataOptions,
  ...chartDataLabelOptions,
  ...chartPieOptions,
}

/** Resolve pie, data, and label options from override, then content-node state. */
const setupInput = () =>
  RenderPipeline.updateInput((input, ctx) =>
    pipe(
      input,
      Options.fallback(pieFeatureOptions, ChartDataParts.getChartState()(ctx)),
      Options.applyDefaults(pieFeatureOptions),
      Options.validate(pieFeatureOptions),
    ),
  )

/** Build the pie slice model from the pie series. */
const setPie = () =>
  RenderPipeline.updateOutput(
    (
      output: ChartSeriesOutput & Partial<ChartPieOutput>,
      ctx: {
        input: ChartPieInput & {
          colorPalette?: ChartColorPalette
          dataTopN?: number
          dataSort?: ChartDataSort
          showDataLabels?: boolean
        }
      },
    ) => {
      const pieSeries = output.series.find(
        ChartSeriesUtils.isVisualization('pie'),
      )
      if (pieSeries == null) {
        return {}
      }

      const colorPattern = Colors.mapColorPalette(ctx.input.colorPalette)
      const sourcePoints = pieSeries.points.map(point => ({
        ...point,
        label: point.x,
      }))
      const slices = aggregatePieSlices({
        points: sourcePoints,
        dataTopN: ctx.input.dataTopN,
        dataSort: ctx.input.dataSort ?? 'natural',
        otherThresholdPct:
          ctx.input.otherThresholdPct ??
          ChartPieConstants.defaultOtherThresholdPct,
        colorPattern,
        otherColorLight: colors.presets.neutral['40'],
        otherColorDark: colors.presets.neutral['60'],
      })
      const total = slices.reduce((sum, slice) => sum + slice.value, 0)
      const isDonut = ctx.input.isDonut ?? ChartPieConstants.defaultIsDonut
      const innerRadius = isDonut
        ? ChartPieConstants.donutInnerRadius
        : ChartPieConstants.pieInnerRadius
      const outerRadius = isDonut
        ? ChartPieConstants.donutOuterRadius
        : ChartPieConstants.pieOuterRadius
      const diameter =
        ctx.input.pieDiameter ?? ChartPieConstants.defaultDiameter
      const plotWidth =
        (outerRadius +
          ChartPieConstants.percentLabelOffset +
          ChartPieConstants.labelMargin) *
        2

      return {
        pie: {
          slices,
          total,
          innerRadius,
          outerRadius,
          diameter,
          showDataLabels: ctx.input.showDataLabels,
          showCenterTotal:
            ctx.input.showCenterTotal ??
            ChartPieConstants.defaultShowCenterTotal,
          minLabelPct:
            ctx.input.minLabelPct ?? ChartPieConstants.defaultMinLabelPct,
          isDonut,
          centerTotalCaption:
            ctx.input.centerTotalCaption ??
            ChartPieConstants.defaultCenterTotalCaption,
          measureLabel: pieSeries.label,
          formatDisplayValue: pieSeries.formatDisplayValue,
          plotWidth,
        },
      }
    },
  )

export const ChartPieInputParts = {
  setupInput,
}

export const ChartPieOutputParts = {
  setPie,
}

export const ChartPieUtils = {
  validValue,
  sortPoints,
  partitionVisibleAndOther,
  aggregatePieSlices,
  computeSliceAngles,
  percentLabelPosition,
  otherColorLight: colors.presets.neutral['40'],
  otherColorDark: colors.presets.neutral['60'],
}
